import React, { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Polygon, PointCoordinates } from 'models/map'
import { useCreateProject, useCreatePondera } from 'services/project'
import { CreateProjectParams, Project, AreaType } from 'models/project'
import { Confirmation } from '../shared/Confirmation'
import { AreaTools, AreaSelection } from 'models/editor'
import { IsochroneDialog } from '../shared/IsochroneDialog'
import { RegionDialog } from '../shared/RegionDialog'
import { RegionFormParams } from 'models/region'
import { Dialog } from 'components/Dialog'
import { Heading } from 'components/Heading'
import { Stack } from 'components/Stack'
import { useTranslation } from 'react-i18next'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { Text } from 'components/Text'
import { getCircleData } from 'utils/areaDetails'
import { useProjectContext } from 'context/project-context'
import { useMapEditorContext } from 'context/map-editor-context'
import { FileUploadDialog } from '../shared/FileUploadDialog'
import { PonderaRadio } from '../shared/PonderaRadio'

export const CreateProject = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createProject, { status }] = useCreateProject()
  const [createPondera] = useCreatePondera()
  const [btnstat, setBtnStat] = useState(false)

  const [{ editor, areaTool, confirmed }, mapEditorDispatch] = useMapEditorContext()
  const [{ activeLayers, hiddenLayers, filters }] = useProjectContext()

  const selectedFeature = editor.feature!
  const [projectData, setProjectData] = useState<CreateProjectParams>()

  const submitProject = (data: CreateProjectParams) => {
    if (areaTool === AreaTools.POINT_PONDERA) {
      //alert(String(areaTool))
      return createPondera(
        { ...data, activeLayers, hiddenLayers, filters },
        {
          onSuccess: (project: Project) => {
            //alert("Success")
            setBtnStat(false)
            console.log(project)
            mapEditorDispatch.featureCreated()
            navigate(`/projects/${project.id}`)
          },
        },
      )
    } else {
      return createProject(
        { ...data, activeLayers, hiddenLayers, filters },
        {
          onSuccess: (project: Project) => {
            console.log(project)
            mapEditorDispatch.featureCreated()
            navigate(`/projects/${project.id}`)
          },
        },
      )
    }

  }

  const onNameFormSubmit = (event: FormEvent) => {
    setBtnStat(true)
    event.preventDefault()
    projectData && submitProject(projectData)
  }

  // isochrones dialog
  const createPointArea = (data: CreateProjectParams) => {
    setProjectData({
      ...data,
      coordinates: selectedFeature.geometry.coordinates as PointCoordinates,
    })
  }

  // Pondera dialog
  const createPonderaProject = (data: CreateProjectParams) => {
    setProjectData({
      ...data,
      coordinates: selectedFeature.geometry.coordinates as PointCoordinates,
    })
  }

  const createPolygon = (areaType: AreaType) => {
    mapEditorDispatch.setConfirm(true)
    setProjectData({ areaType, geometry: selectedFeature.geometry as Polygon })
  }

  // With radius tool
  const createRadius = (areaType: AreaType) => {
    const { circleCenter, radius } = getCircleData(selectedFeature as any)
    mapEditorDispatch.setConfirm(true)
    setProjectData({
      areaType,
      coordinates: circleCenter as PointCoordinates,
      radius,
    })
  }

  const createRegion = (data: RegionFormParams) => {
    setProjectData({ areaType: AreaType.REGION, region: data })
  }

  if (!confirmed) {
    const onConfirmFor = (areaTool: AreaSelection) => {
      switch (areaTool) {
        case AreaTools.RADIUS:
          return () => createRadius(AreaType.RADIUS)
        case AreaTools.POLYGON:
          return () => createPolygon(AreaType.POLYGON)
        default:
          return () => mapEditorDispatch.setConfirm(true)
      }
    }

    return <Confirmation onConfirm={onConfirmFor(areaTool)} />
  }

  if (projectData) {
    return (
      <Dialog
        isOpen={true}
        aria-label={t('project_name_dialog.title')}
        onClose={() => setProjectData(undefined)}
      >
        <Stack space="medium">
          <>
            <Heading size="secondary">{t('project_name_dialog.title')}</Heading>
            <Text>{t('project_name_dialog.desc')}</Text>
          </>
          <form onSubmit={onNameFormSubmit}>
            <Stack space="medium">
              <TextInput
                name="project_name"
                label={t('project_name_dialog.form.name')}
                value={projectData.name}
                onChange={(event) => setProjectData({ ...projectData, name: event.target.value })}
              />
              {areaTool === AreaTools.POINT_PONDERA && <Button full type="submit" loading={btnstat}>
                {t('project_name_dialog.cta')}
              </Button>}

              {areaTool !== AreaTools.POINT_PONDERA && <Button full type="submit" loading={status === 'loading'}>
                {t('project_name_dialog.cta')}
              </Button>}


            </Stack>
          </form>
        </Stack>
      </Dialog>
    )
  }

  if (areaTool === AreaTools.POINT) {
    return (
      <IsochroneDialog
        onClose={() => mapEditorDispatch.setConfirm(false)}
        onConfirm={createPointArea}
      />
    )
  }

  if (areaTool === AreaTools.POINT_PONDERA) {
    return (
      <PonderaRadio
        onClose={() => mapEditorDispatch.setConfirm(false)}
        onConfirm={createPonderaProject}
      />
    )
  }

  if (areaTool === AreaTools.REGION) {
    return (
      <RegionDialog onClose={() => mapEditorDispatch.featureCreated()} onConfirm={createRegion} />
    )
  }

  if (areaTool === AreaTools.FILE_UPLOAD) {
    return (
      <FileUploadDialog
        onClose={() => mapEditorDispatch.featureCreated()}
        onConfirm={submitProject}
        status={status}
      />
    )
  }

  return null
}

CreateProject.displayName = 'CreateProject'
